import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment/moment";
import { getS3URL } from "../../../_helpers";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import CartButton from "../../Courses/CourseTile/CartButton/CartButton";
import Share from "../../Courses/CourseShare/courseShare";
import { Heart, Share2 } from "lucide-react";
import { deleteWishlist, addWishlist } from "../../../_actions/cart.actions";
import { courseActions } from "../../../_actions";
import "../../Program/ExploreProgram.scss";

const slugify = (text) => {
  return text
    .toString()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^\w\-]+/g, "")
    .replace(/\-\-+/g, "-")
    .replace(/^-+/, "")
    .replace(/-+$/, "");
};

const stripHtml = (html) => {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent || "";
};

const getShortDescription = (item) => {
  if (item.courseOneLiner) return item.courseOneLiner;
  const plainText = stripHtml(item.Description);
  return plainText.length > 120 ? plainText.substring(0, 120) + "..." : plainText;
};

const MostPopularCourse = ({ category, course }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loggedIn } = useSelector((state) => state?.authentication || {});
  const [shareCourse, setShareCourse] = useState(null);

  const handleViewProgram = (item) => {
    const slug = slugify(item.CourseName);
    navigate(`/${slug}`, { state: { course: item } });
  };

  const likeHandler = (e, courseId, isWishlisted) => {
    e.stopPropagation(); // Prevent card navigation
    if (loggedIn) {
      dispatch(courseActions.handleResetCourseList());
      if (isWishlisted) {
        dispatch(deleteWishlist(courseId));
      } else {
        dispatch(addWishlist(courseId));
      }
    } 
    else {
      navigate("/login");
    }
  };

  if (!course || course.length === 0) return null;

  return (
    <>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <h2 className="text-3xl font-extrabold text-gray-950 mb-4 tracking-tight">
            yslearn's Most Popular {category?.categoryName} Courses
          </h2>
          <div className="w-16 h-1 bg-blue-600 mx-auto mb-4 rounded-full"></div>
          <p className="text-lg text-gray-600 font-normal">
            Take the first step towards career success with our top {category?.categoryName} courses, built to prepare you for the job market.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {course.map((item, index) => (
            <div
              key={item?.id || index}
              className="course-card"
              onClick={() => handleViewProgram(item)}
            >
              {/* Tag Ribbon */}
              <div className="ribbon-wrapper">
                <span className="ribbon-tag">
                  {item.courseType || "Online"}
                </span>
              </div>

              {/* Floating Action Overlay (Wishlist & Share) */}
              <div className="card-floating-actions" onClick={(e) => e.stopPropagation()}>
                <button
                  className={`floating-btn wishlist-btn ${item?.isWishlisted ? "active" : ""}`}
                  onClick={(e) => likeHandler(e, item.ID, item?.isWishlisted)}
                  title={item?.isWishlisted ? "Remove from Wishlist" : "Add to Wishlist"}
                >
                  <Heart size={15} fill={item?.isWishlisted ? "#ef4444" : "none"} stroke={item?.isWishlisted ? "#ef4444" : "currentColor"} />
                </button>
                <button
                  className="floating-btn share-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShareCourse(item);
                  }}
                  title="Share Program"
                >
                  <Share2 size={15} />
                </button>
              </div>

              <div className="card-top-row">
                <div className="image-container">
                  <img
                    src={getS3URL(item?.imageFiles?.[0]?.fileName)}
                    alt={item?.CourseName}
                    className="course-image"
                    loading="lazy"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = require("../../../assets/images/bgimages.webp");
                    }}
                  />
                </div>
                <h2 className="course-title">{item.CourseName}</h2>
              </div>

              <div className="card-stats-row">
                <span className="stat-item">
                  <strong>Duration:</strong> {item.Duration} hrs
                </span>
                <span className="stat-item">
                  <strong>Starts:</strong>{" "}
                  {moment(item?.Createdat || item?.CreateOn).format("MMM DD, YYYY")}
                </span>
              </div>

              <div className="card-description">
                <p>{getShortDescription(item)}</p>
              </div>

              <div className="card-actions-row">
                <button
                  className="view-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewProgram(item);
                  }}
                >
                  View Course
                </button>
                <div className="buy-button-wrapper" onClick={(e) => e.stopPropagation()}>
                  {loggedIn ? (
                    <CartButton post={item} />
                  ) : (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate("/login");
                      }}
                    >
                      <span className="add-to-cart">{t("addToCart")}</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {shareCourse && (
        <Share
          shareUrl={`${window.location.origin}/${slugify(shareCourse.CourseName)}`}
          handleClose={() => setShareCourse(null)}
        />
      )}
    </>
  );
};

export default MostPopularCourse;
